/**
 * Which Hey Nova model listens, and how sure it has to be.
 *
 * Chosen on 26 Oct 2026 by running both of the project's models through
 * openWakeWord's own pipeline: "Hey Nova" from six natural macOS voices at
 * three speeds, and 20 other phrases from the same voices.
 *  - April's model at 0.4, two chunks in a row: woke on 33 of 36, and falsely
 *    on 3 of 120 — all near-rhymes, "Hey Noah" and "Hey Nora".
 *  - August's model never scores above 0.68; at 0.3 it woke on 34 and falsely
 *    on 11.
 * Neither wakes on "Hey, Nova" said with a pause between the words. Real
 * voices in a real bay are the test still to run, and these numbers are the
 * ones to tune after it. web/wakeword/models/NOTICE.txt has the files.
 */
export const WAKE_WORD = {
  /** Served from /wakeword/models/. */
  model: 'hey_nova_20260413_031510.onnx',
  /** The score, 0 to 1, a chunk must reach. */
  threshold: 0.4,
  /** Consecutive 80 ms chunks over the threshold before it counts. */
  patience: 2,
  /** Embeddings the model reads: 16, about 1.3 seconds. */
  frames: 16,
};

/** Where the web build serves the models, the audio worklet and ONNX Runtime's WebAssembly. */
export const WAKE_WORD_BASE = '/wakeword/';

/**
 * The engines a tester can pick between in the chat header: the project's own
 * openWakeWord model above, and Picovoice's Porcupine (porcupineEngine.web.ts
 * in the browser, porcupineEngine.ios.ts on iOS), trialled from 28 Oct 2026.
 * Both hear the same microphone.
 */
export type WakeWordEngineName = 'openwakeword' | 'picovoice';
export const WAKE_WORD_ENGINE_LABELS: Record<WakeWordEngineName, string> = {
  openwakeword: 'openWakeWord',
  picovoice: 'Picovoice',
};

/**
 * Porcupine's files, in web/wakeword/picovoice: the "Hey Nova" keyword made on
 * Picovoice Console (v4.0.0), once for WebAssembly — browsers — and once for
 * iOS, and Porcupine's English model (v4.0) for the browser; on iOS the same
 * model comes in Porcupine's pod. A keyword works only with an AccessKey from
 * the account that made it. Sensitivity runs 0 to 1: higher wakes more
 * readily, and falsely more often; 0.5 is Picovoice's default. `version` goes
 * up when a web file changes, so the copy Porcupine keeps in IndexedDB is
 * replaced.
 */
export const PICOVOICE = {
  keyword: 'hey-nova_en_wasm_v4_0_0.ppn',
  /** porcupineEngine.ios.ts names it again, for Metro. */
  iosKeyword: 'hey-nova_en_ios_v4_0_0.ppn',
  model: 'porcupine_params.pv',
  sensitivity: 0.5,
  version: 1,
};
